import produce from 'immer'
import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import PermissionsList from '../../../components/common/allPermissions/PermissionsList'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import userManagementService from '../../../services/userManagementService'
import { changeLoadingRequest } from '../../../store/app/actionTypes'
import {
  deselectAllActionsRequest,
  SetModifyPermissionListRequest,
} from '../../../store/permissions/actions'
import { getSelectedPackages } from '../../../store/permissions/selectors'
// import { selectedPermisionsByRoleId } from '../../../store/permissions/selectors'
import { IRole } from '../../../store/usermanagement/actionTypes'
import {
  ALERT_SUCCESS,
  ALERT_WARNING,
  TOAST_POSITION_TOP_RIGHT,
  TOAST_TRANSITION_SLIDE,
} from '../../../utils/constants'
import {
  roleNameEmpty,
  roleNameLength,
  roleDescription,
  rolePermission,
  validationError,
} from '../../../utils/errorMessages'
import { roleModify } from '../../../utils/successMessages'
import { showTotast } from '../../../utils/toast'
import { handleNotification } from '../../../utils/utilFunctions'
//import { getRolesById } from '../../../store/usermanagement/selectors'
type TParams = { id: string }

interface FormErrorModel {
  roleNameError: string
  roleDescriptionError: string
  rolePermissionError: string
}

const ModifyRolePermissions = ({ match }: RouteComponentProps<TParams>) => {
  const [role, setRole] = useState({} as IRole)
  const [showConfirmation, seShowConfirmation] = useState(false)
  const [errors, setErrors] = useState({} as FormErrorModel)
  const dispatch = useDispatch()
  const history = useHistory()
  const selectedPackagesList = useSelector(getSelectedPackages)
  useEffect(() => {
    async function getRole() {
      const checkLock = await userManagementService.apiRoleLock(
        parseInt(match.params.id)
      )

      if (checkLock.status === 400) {
        handleNotification(checkLock, '')
        history.goBack()
      }

      const roleData = await userManagementService.apiFetchRole(
        parseInt(match.params.id)
      )

      setRole(roleData.data)

      dispatch(
        SetModifyPermissionListRequest({
          permissions: roleData.data.permissions.packageList,
        })
      )
    }
    getRole()
  }, [])

  const findFormErrors = () => {
    const newErrors = {} as FormErrorModel

    // Role name error
    if (!role.name || role.name === '') {
      newErrors.roleNameError = roleNameEmpty
    } else if (role.name != null && role.name?.length > 50) {
      newErrors.roleNameError = roleNameLength
    }

    // Role description error
    if (role.description != null && role.description?.length > 250) {
      newErrors.roleDescriptionError = roleDescription
    }

    // Role permission error
    if (role.allPermissions === false && selectedPackagesList == null) {
      newErrors.rolePermissionError = rolePermission
    } else if (
      role.allPermissions === false &&
      selectedPackagesList.length === 0
    ) {
      newErrors.rolePermissionError = rolePermission
    }
    return newErrors
  }

  const handleBack = async () => {
    await userManagementService.apiRoleUnLock(parseInt(match.params.id))
    history.goBack()
  }

  const onSelected = (value: boolean) => {
    if (value == true) {
      const newValue = produce(role, (draft: IRole) => {
        draft.allPermissions = true
      })
      setRole(newValue)
    } else {
      dispatch(deselectAllActionsRequest())
      const newValue = produce(role, (draft: IRole) => {
        draft.allPermissions = false
      })
      setRole(newValue)
    }
  }

  const handleSubmit = () => {
    const newErrors = findFormErrors()
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      showTotast(
        ALERT_WARNING,
        validationError,
        TOAST_POSITION_TOP_RIGHT,
        5,
        TOAST_TRANSITION_SLIDE
      )
    } else {
      setErrors(newErrors)
      seShowConfirmation(true)
    }
  }

  const handleConfirm = async () => {
    const submitObject = produce(role, (draft: IRole) => {
      draft.permissions.packageList = selectedPackagesList
    })
    dispatch(changeLoadingRequest())
    const result = await userManagementService.apiRoleUpdate(
      submitObject.id,
      submitObject
    )
    dispatch(changeLoadingRequest())
    let success = false
    let message = result.errorMessage ? result.errorMessage : ''
    if (result.status === 200) {
      await userManagementService.apiRoleUnLock(parseInt(match.params.id))
      success = true
      message = roleModify
      history.goBack()
    }
    showTotast(
      success ? ALERT_SUCCESS : ALERT_WARNING,
      message,
      TOAST_POSITION_TOP_RIGHT,
      5,
      TOAST_TRANSITION_SLIDE
    )
  }

  return (
    <Row className="create-role-page-layout">
      <Col sm={12} className="border-label">
        <div className="border-label-span">Modify Role</div>
        <Form>
          <Form.Group className="mb-3" controlId="formGroupName">
            <Form.Label>Name</Form.Label>
            <Form.Control
              disabled
              type="text"
              placeholder="Enter Name"
              onChange={(e) => {
                const newValue = produce(role, (draft: IRole) => {
                  draft.name = e.target.value
                })
                setRole(newValue)
              }}
              onBlur={(e) => {
                const newError = produce(errors, (draft) => {
                  if (e.target.value === '') {
                    draft.roleNameError = roleNameEmpty
                  } else if (e.target.value?.length > 50) {
                    draft.roleNameError = roleNameLength
                  } else {
                    draft.roleNameError = ''
                  }
                })
                setErrors(newError)
              }}
              value={role.name}
              isInvalid={!!errors.roleNameError}
            />
            <Form.Control.Feedback type="invalid">
              {errors.roleNameError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formGroupDescription">
            <Form.Label>
              Description <span className="required-field-astrix">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              type="text"
              placeholder="Enter Description"
              onChange={(e) => {
                const newValue = produce(role, (draft: IRole) => {
                  draft.description = e.target.value
                })
                setRole(newValue)
              }}
              value={role.description}
              onBlur={(e) => {
                const newError = produce(errors, (draft) => {
                  if (e.target.value?.length > 250) {
                    draft.roleDescriptionError = roleDescription
                  } else {
                    draft.roleDescriptionError = ''
                  }
                })
                setErrors(newError)
              }}
              isInvalid={!!errors.roleDescriptionError}
            />
            <Form.Control.Feedback type="invalid">
              {errors.roleDescriptionError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formGroupPermission">
            <Form.Label>Permission</Form.Label>
            <Row>
              <Col sm={12}>
                <Container>
                  <Form.Check
                    type="checkbox"
                    label="All Permissions"
                    name="allPermissions"
                    onChange={(e) => {
                      onSelected(e.target.checked)
                    }}
                    checked={role.allPermissions}
                    isInvalid={!!errors.rolePermissionError}
                  />
                </Container>
              </Col>
              {!role.allPermissions && (
                <Col sm={12}>
                  <PermissionsList unEditable={false} />
                </Col>
              )}
            </Row>
            <Form.Control.Feedback type="invalid">
              {errors.rolePermissionError}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Col>
      <Col sm={12} className="d-flex justify-content-end mt-4">
        <button className="cancel-button" onClick={handleBack}>
          Cancel
        </button>
        <button className="save-button" onClick={handleSubmit}>
          Modify
        </button>
      </Col>
      {showConfirmation && (
        <ConfirmationModal
          name={'Confirmation'}
          message={'Are you sure to modify this role ?'}
          onCancel={() => {
            seShowConfirmation(false)
          }}
          onConfirm={handleConfirm}
        />
      )}
    </Row>
  )
}

export default ModifyRolePermissions
